import { fehlertext } from '../../api/client'
import type { Verwaltungshandlung } from '../../api/types'
import { useMe, useVerwaltungsprotokoll } from '../../hooks/queries'
import { groesse } from '../../lib/format'
import { zeitText } from '../../lib/ortszeit'
import { useTexte } from '../../texte'

type Handlungen = ReturnType<typeof useTexte>['administration']['handlung']

/**
 * The sentence for one row -- the server stores the action and small facts,
 * the interface says what they mean. Unknown actions (from a newer server
 * than this interface) show their raw name rather than nothing.
 */
function satz(
  eintrag: Verwaltungshandlung,
  t: Handlungen,
  rollen: Record<string, string>,
  ohneNamen: string,
): string {
  const wen = eintrag.subject ?? ohneNamen
  const d = eintrag.detail
  const zahl = (schluessel: string): number => (typeof d[schluessel] === 'number' ? (d[schluessel] as number) : 0)
  const wort = (schluessel: string): string => (typeof d[schluessel] === 'string' ? (d[schluessel] as string) : '')
  switch (eintrag.action) {
    case 'role_set':
      return t.role_set(wen, rollen[wort('role')] ?? wort('role'))
    case 'supervisor_set':
      return t.supervisor_set(wen)
    case 'supervisor_cleared':
      return t.supervisor_cleared(wen)
    case 'name_set':
      return t.name_set(wen, wort('name'))
    case 'account_disabled':
      return t.account_disabled(wen)
    case 'account_enabled':
      return t.account_enabled(wen)
    case 'account_deleted':
      return t.account_deleted(wen)
    case 'trip_reset':
      return t.trip_reset(wen, zahl('messages'), zahl('media'))
    case 'trip_deleted':
      return t.trip_deleted(wen, zahl('messages'), zahl('media'))
    case 'member_removed':
      return t.member_removed(wen, wort('name') || ohneNamen)
    case 'cutoff_set':
      return t.cutoff_set(wen, wort('name') || ohneNamen)
    case 'cutoff_cleared':
      return t.cutoff_cleared(wen, wort('name') || ohneNamen)
    case 'settings_changed':
      return t.settings_changed(Array.isArray(d.keys) ? (d.keys as string[]).join(', ') : '')
    case 'storage_swept':
      return t.storage_swept(zahl('files'), groesse(zahl('bytes')))
    case 'missing_swept':
      return t.missing_swept(zahl('files'))
    case 'orphan_deleted':
      return t.orphan_deleted(wen, groesse(zahl('bytes')))
    case 'database_swept':
      return t.database_swept(zahl('total'))
    default:
      return t.sonst(eintrag.action)
  }
}

/**
 * Who did what, and when (1.7.0) -- the record of administrative actions,
 * the last hundred, newest first. Built like the `.env` log under Betrieb,
 * and on the same rows (`.envzeile`): one log looks like the other.
 */
export function VerwaltungsprotokollCard() {
  const texte = useTexte()
  const t = texte.administration
  const me = useMe()
  const protokoll = useVerwaltungsprotokoll(me.data?.may_manage_people === true)

  if (protokoll.isPending) return <p className="card__lead leise">…</p>
  if (protokoll.isError) {
    return (
      <p className="notice" role="alert">
        {fehlertext(protokoll.error)}
      </p>
    )
  }
  const eintraege = protokoll.data.entries

  return (
    <div className="card card--seite" data-verwaltungsprotokoll="">
      <p className="card__lead">{t.protokollLead}</p>
      {eintraege.length === 0 && <p className="leise">{t.protokollLeer}</p>}
      <div className="stack stack--eng">
        {eintraege.map((eintrag) => (
          <div className="envzeile" key={eintrag.id} data-handlung={eintrag.action}>
            <div className="envzeile__kopf">
              <span className="envzeile__quelle">
                {t.protokollZeile(eintrag.by ?? t.protokollUnbekannt, zeitText(eintrag.at))}
              </span>
            </div>
            <p className="envzeile__satz">{satz(eintrag, t.handlung, t.rollen, t.protokollOhneNamen)}</p>
          </div>
        ))}
      </div>
    </div>
  )
}
